import { isWebStudioAuthSessionLossDiagnostic, readWebStudioPublicBootConfig } from "./bootConfig";
import {
  validateWebStudioRequest,
  validateWebStudioResponse,
  type WebStudioCloudApiOperation,
  type WebStudioCloudApiRequestByOperation,
  type WebStudioCloudApiResponseByOperation,
  type WebStudioCloudApiTransport,
  type WebStudioCloudApiTransportRequest,
  webStudioCloudApiOperations,
} from "./generatedClient";

export interface WebStudioTransportConfig {
  baseUrl?: string;
  allowedCredentialOrigins?: readonly string[];
  getInMemoryTestBearerToken?: () => string | null | undefined;
}

/**
 * Mid-session account-session loss channel: when an operation response
 * carries a typed `web_studio.auth.*` session-loss diagnostic (signed out,
 * expired, revoked — never narrower scope-loss codes), subscribers learn the
 * code so the session flow can route to the sign-in gate. The transport only
 * reports what the backend typed; it decides nothing itself.
 */
type WebStudioAuthSessionLossListener = (diagnosticCode: string) => void;

const authSessionLossListeners = new Set<WebStudioAuthSessionLossListener>();

export function subscribeWebStudioAuthSessionLoss(
  listener: WebStudioAuthSessionLossListener,
): () => void {
  authSessionLossListeners.add(listener);
  return () => {
    authSessionLossListeners.delete(listener);
  };
}

function notifyAuthSessionLoss(payload: unknown): void {
  if (typeof payload !== "object" || payload === null) {
    return;
  }
  const diagnostics = (payload as { diagnostics?: unknown }).diagnostics;
  if (!Array.isArray(diagnostics)) {
    return;
  }
  for (const entry of diagnostics) {
    const code = (entry as { diagnostic?: { code?: unknown } })?.diagnostic?.code;
    if (typeof code === "string" && isWebStudioAuthSessionLossDiagnostic(code)) {
      for (const listener of authSessionLossListeners) {
        listener(code);
      }
      return;
    }
  }
}

export class WebStudioTransportError extends Error {
  constructor(
    message: string,
    readonly operation: WebStudioCloudApiOperation,
    readonly status?: number,
  ) {
    super(message);
    this.name = "WebStudioTransportError";
  }
}

/**
 * Resolves the cross-origin management target baked into the artifact via
 * `VITE_JACQOS_WEB_STUDIO_ORIGIN`, or undefined for same-origin-management
 * builds. Hosted deployments (Cloudflare Pages) serve the bundle from a
 * different origin than the management plane, so the configured origin must
 * drive both request routing and credential admission.
 */
export function resolveConfiguredApiOrigin(
  env: Record<string, string | boolean | undefined> = import.meta.env,
): string | undefined {
  const { origin } = readWebStudioPublicBootConfig(env);
  return origin === "same-origin-management" ? undefined : new URL(origin).origin;
}

export function createWebStudioFetchTransport(
  config: WebStudioTransportConfig = {},
): WebStudioCloudApiTransport {
  const configuredOrigin = config.baseUrl === undefined ? resolveConfiguredApiOrigin() : undefined;
  const resolved: WebStudioTransportConfig = {
    ...config,
    baseUrl: config.baseUrl ?? configuredOrigin ?? "",
    ...(configuredOrigin
      ? {
          allowedCredentialOrigins: [...(config.allowedCredentialOrigins ?? []), configuredOrigin],
        }
      : {}),
  };
  return {
    async request<Operation extends WebStudioCloudApiOperation>(
      input: WebStudioCloudApiTransportRequest<Operation>,
    ): Promise<WebStudioCloudApiResponseByOperation[Operation]> {
      // Fail closed before any bytes leave the browser: the logical request
      // (the makeWebStudioRequest product carried on input.request) must
      // satisfy the generated contract — including per-operation actor,
      // idempotency-key, and expected-state requirements — or the call is
      // a client defect, never something the backend should have to reject.
      const requestIssues = validateWebStudioRequest(input.operation, input.request);
      if (requestIssues.length > 0) {
        throw new WebStudioTransportError(
          `invalid Web Studio request: ${requestIssues.map((issue) => `${issue.path} ${issue.message}`).join("; ")}`,
          input.operation,
        );
      }
      const url = buildUrl(input, resolved.baseUrl ?? "");
      assertCredentialOriginAllowed(url, resolved);
      const headers = new Headers({
        Accept: "application/json",
        "X-JacqOS-Web-Studio-Operation": input.operationId,
        "X-JacqOS-Web-Studio-Contract": input.operationContract,
      });
      const bearer = resolved.getInMemoryTestBearerToken?.();
      if (bearer?.trim()) {
        headers.set("Authorization", `Bearer ${bearer}`);
      }
      const init: RequestInit = {
        method: input.method,
        headers,
        credentials: "include",
        cache: "no-store",
      };
      if (input.method === "POST") {
        headers.set("Content-Type", "application/json");
        init.body = JSON.stringify({
          ...input.request.body,
          target: input.request.target,
          organization_id: input.request.target.organizationId,
          workspace_id: input.request.target.workspaceId,
          environment_id: input.request.target.environmentId,
        });
      }

      const response = await fetch(url, init);
      const payload = await readJsonResponse<Operation>(response, input.operation);
      const issues = validateWebStudioResponse(input.operation, payload);
      if (issues.length > 0) {
        throw new WebStudioTransportError(
          `invalid Web Studio response: ${issues.map((issue) => `${issue.path} ${issue.message}`).join("; ")}`,
          input.operation,
          response.status,
        );
      }
      // Boot classification already owns boot-level auth state; the listener
      // channel exists for operation-level typed session-loss diagnostics.
      if (input.operation !== "webStudioBoot") {
        notifyAuthSessionLoss(payload);
      }
      return payload;
    },
  };
}

function assertCredentialOriginAllowed(url: string, config: WebStudioTransportConfig): void {
  const target = new URL(url, window.location.origin);
  if (target.origin === window.location.origin) {
    return;
  }
  const allowed = new Set(config.allowedCredentialOrigins ?? []);
  if (!allowed.has(target.origin)) {
    throw new WebStudioTransportError(
      `credentialed Web Studio request origin is not allowlisted: ${target.origin}`,
      "webStudioBoot",
    );
  }
}

function buildUrl<Operation extends WebStudioCloudApiOperation>(
  input: WebStudioCloudApiTransportRequest<Operation>,
  baseUrl: string,
): string {
  const url = new URL(input.path, baseUrl || window.location.origin);
  const query = input.request.query ?? {};
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) {
      url.searchParams.set(key, String(value));
    }
  }
  url.searchParams.set("organization_id", input.request.target.organizationId);
  url.searchParams.set("workspace_id", input.request.target.workspaceId);
  url.searchParams.set("environment_id", input.request.target.environmentId);
  if (input.request.target.lineageId) {
    url.searchParams.set("lineage_id", input.request.target.lineageId);
  }
  if (input.request.target.activationId) {
    url.searchParams.set("activation_id", input.request.target.activationId);
  }
  return url.toString();
}

async function readJsonResponse<Operation extends WebStudioCloudApiOperation>(
  response: Response,
  operation: Operation,
): Promise<WebStudioCloudApiResponseByOperation[Operation]> {
  const text = await response.text();
  try {
    return JSON.parse(text) as WebStudioCloudApiResponseByOperation[Operation];
  } catch {
    throw new WebStudioTransportError(
      `Web Studio ${webStudioCloudApiOperations[operation].operationId} returned non-JSON response`,
      operation,
      response.status,
    );
  }
}

/**
 * Builds the generated request envelope for one operation. Attribution is
 * never fabricated here: operations whose contract requires an actor (or a
 * full mutation envelope) receive those fields from the calling adapter via
 * `overrides`, and the transport's request validation fails closed when a
 * required field is missing.
 */
export function makeWebStudioRequest<Operation extends WebStudioCloudApiOperation>(
  operation: Operation,
  target: WebStudioCloudApiRequestByOperation[Operation]["target"],
  overrides: Partial<WebStudioCloudApiRequestByOperation[Operation]> = {},
): WebStudioCloudApiRequestByOperation[Operation] {
  const contract = webStudioCloudApiOperations[operation];
  return {
    schemaVersion: "jacqos.contracts.v1",
    operation: contract.operationId,
    operationContract: contract.operationContract,
    target,
    authority: {
      authorityKind: "managed_cloud",
      runtimeRole: "browser_client",
      authorityEvidenceDigest: "sha256:browser-client-awaiting-backend",
      lineageId: target.lineageId,
      activationId: target.activationId,
    },
    params: {},
    ...overrides,
  } as WebStudioCloudApiRequestByOperation[Operation];
}
